import { useMemo } from 'react';

import { useGetADFilters } from '@/hooks/api/useGetADFilters';
import { useScopedAssetFilters } from '@/hooks/api/useGetAssetFilters';
import { FilterOption } from '@/hooks/api/useGetAssetFilters';
import { useGetSeedFilters } from '@/hooks/api/useGetSeedFilters';
import { useGetTechnologyFilters } from '@/hooks/api/useGetTechnologyFilters';
import {
  useGetVulnerabilityFilters,
  VULNERABILITY_FIELD_NAMES,
} from '@/hooks/api/useGetVulnerabilityFilters';
import { getCorrectedADFilters } from '@/sections/insights/queryBuilder/legacy/entities/activeDirectory/adFilterConfig';
import { Option } from '@/sections/insights/queryBuilder/legacy/types/queryBuilder.types';
import { SEED_FIELDS } from '@/utils/mappings.util';

// Entity attributes mapping for static entities
const ENTITY_ATTRIBUTES_MAP: Record<string, Option[]> = {
  Technology: [
    { value: 'vendor', label: 'Vendor' },
    { value: 'product', label: 'Product' },
    { value: 'version', label: 'Version' },
  ],
};

// Supported entity types
export type EntityType =
  | 'Asset'
  | 'Technology'
  | 'Vulnerability'
  | 'Seed'
  | 'ActiveDirectory';

// Extended context types for all entities
export type EntityFilterContext =
  | 'query-builder'
  | 'asset-table'
  | 'tech-table'
  | 'vuln-table'
  | 'seed-table'
  | 'ad-table'
  | 'graph-explorer';

// Query status type matching the actual hook return types
export type QueryStatus = 'idle' | 'pending' | 'success' | 'error' | string;

// Standard entity filter response
export interface EntityFilterResponse {
  filterOptions: Record<string, unknown> | Option[] | null;
  availableFilters?: Record<string, FilterOption[]> | null;
  sortedOptions: Option[];
  status: QueryStatus;
}

// Export FilterOption type for compatibility
export type { FilterOption } from '@/hooks/api/useGetAssetFilters';

// Entity-specific label mappings
const ENTITY_LABEL_MAPS: Record<EntityType, Record<string, string>> = {
  Asset: {
    asname: 'AS Name',
    asnumber: 'AS Number',
    attackSurface: 'Attack Surface',
    class: 'IP Version',
    country: 'Country',
    domain: 'Domain',
    new: 'Recent',
    origins: 'Origin',
    port: 'Port Number',
    profile: 'Scan Profile',
    protocol: 'Protocol',
    source: 'Source',
    status: 'Status',
    tags: 'Tag',
  },
  Technology: {
    vendor: 'Vendor',
    product: 'Product',
    version: 'Version',
    tags: 'Tag',
  },
  Vulnerability: {
    name: 'Name',
    priority: 'Priority',
    severity: 'Severity',
    status: 'Status',
    origin: 'Origin',
    tags: 'Tags',
    frameworks: 'Frameworks',
    attackSurface: 'Attack Surface',
    cve: 'CVE',
    cvss: 'CVSS Score',
    epss: 'EPSS Score',
    kev: 'CISA KEV',
  },
  Seed: {
    source: 'Origin',
    type: 'Type',
    status: 'Status',
    profile: 'Scan Profile',
  },
  ActiveDirectory: {
    // AD-specific labels will come from getCorrectedADFilters()
  },
};

// Context-based filter exclusions for each entity type
const ENTITY_CONTEXT_EXCLUSIONS: Record<
  EntityType,
  Record<EntityFilterContext, string[]>
> = {
  Asset: {
    'query-builder': ['new', 'attackSurface'],
    'asset-table': [],
    'graph-explorer': ['new'],
    'tech-table': [],
    'vuln-table': [],
    'seed-table': [],
    'ad-table': [],
  },
  Technology: {
    'query-builder': [],
    'tech-table': [],
    'asset-table': [],
    'graph-explorer': [],
    'vuln-table': [],
    'seed-table': [],
    'ad-table': [],
  },
  Vulnerability: {
    'query-builder': [],
    'vuln-table': [],
    'asset-table': [],
    'tech-table': [],
    'graph-explorer': [],
    'seed-table': [],
    'ad-table': [],
  },
  Seed: {
    'query-builder': [],
    'seed-table': [],
    'asset-table': [],
    'tech-table': [],
    'vuln-table': [],
    'graph-explorer': [],
    'ad-table': [],
  },
  ActiveDirectory: {
    'query-builder': [],
    'ad-table': [],
    'asset-table': [],
    'tech-table': [],
    'vuln-table': [],
    'seed-table': [],
    'graph-explorer': [],
  },
};

/**
 * Generates a human-readable label for any entity field
 */
export function getEntityFieldLabel(
  entityType: EntityType,
  key: string
): string {
  const entityLabels = ENTITY_LABEL_MAPS[entityType];
  return entityLabels[key] || generateLabel(key);
}

/**
 * Generates a label from a key by converting camelCase/snake_case to Title Case
 */
function generateLabel(key: string): string {
  return key
    .replace(/([a-z])([A-Z])/g, '$1 $2') // camelCase -> camel Case
    .replace(/_/g, ' ') // snake_case -> snake case
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

/**
 * Applies context-based exclusions to filter options
 */
function applyContextExclusions<T>(
  data: Record<string, T>,
  entityType: EntityType,
  context: EntityFilterContext
): Record<string, T> {
  const exclusions = ENTITY_CONTEXT_EXCLUSIONS[entityType]?.[context] || [];

  if (exclusions.length === 0) return data;

  return Object.fromEntries(
    Object.entries(data).filter(([key]) => !exclusions.includes(key))
  );
}

/**
 * Converts entity data to sorted option array
 */
export function getEntitySortedOptions(
  entityType: EntityType,
  data: unknown,
  status: QueryStatus
): Option[] {
  if (status !== 'success' || !data) {
    return [];
  }

  let options: Option[] = [];

  switch (entityType) {
    case 'Asset':
      if (typeof data === 'object' && !Array.isArray(data)) {
        options = Object.keys(data).map(key => ({
          value: key,
          label: getEntityFieldLabel(entityType, key),
        }));
      }
      break;

    case 'Technology':
      options = ENTITY_ATTRIBUTES_MAP.Technology || [];
      break;

    case 'Vulnerability': {
      const fieldNames = Object.values(VULNERABILITY_FIELD_NAMES) as string[];
      // Filter out 'name' and 'priority' - these are search/sort fields, not filter dropdowns
      const filterFields = fieldNames.filter(
        fieldName => fieldName !== 'name' && fieldName !== 'priority'
      );
      options = filterFields.map(fieldName => ({
        value: fieldName,
        label: getEntityFieldLabel(entityType, fieldName),
      }));
      break;
    }

    case 'Seed': {
      options = SEED_FIELDS.map(field => ({
        value: field.name,
        label: field.label, // SEED_FIELDS already has proper labels
      }));
      break;
    }

    case 'ActiveDirectory': {
      const correctedFilters = getCorrectedADFilters();
      options = correctedFilters.map(adFilter => ({
        value: adFilter.key,
        label: adFilter.label,
      }));
      break;
    }

    default:
      return [];
  }

  // Sort alphabetically by label
  return options.sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Universal entity filter hook
 * Works for any entity type and any table context
 */
export function useEntityFilters(
  entityType: EntityType,
  context: EntityFilterContext = 'query-builder'
): EntityFilterResponse {
  // Asset filters (with context awareness)
  const { data: assetData, status: assetStatus } = useScopedAssetFilters(
    entityType === 'Asset'
      ? context === 'query-builder'
        ? 'query-builder'
        : 'asset-table'
      : 'asset-table'
  );

  // Technology filters
  const { data: techData, status: techStatus } = useGetTechnologyFilters();

  // Vulnerability filters
  const { data: vulnData, status: vulnStatus } = useGetVulnerabilityFilters();

  // Seed filters
  const { data: seedData, status: seedStatus } = useGetSeedFilters();

  // ActiveDirectory filters
  const { data: adData } = useGetADFilters();

  // Select the appropriate data and status based on entity type
  const { data, status } = useMemo(() => {
    switch (entityType) {
      case 'Asset':
        return { data: assetData, status: assetStatus };
      case 'Technology':
        return { data: techData, status: techStatus };
      case 'Vulnerability': {
        // Map API response keys (with 'Filters' suffix) to expected filter keys
        // This adapter pattern maintains compatibility with useEntityFilters standardized API
        const mappedData = vulnData
          ? {
              severity: vulnData.severityFilters,
              status: vulnData.statusFilters,
              origin: vulnData.sourceFilters,
              tags: vulnData.tagFilters,
              frameworks: vulnData.frameworkFilters,
              attackSurface: vulnData.attackSurfaceFilters,
              kev: vulnData.kevFilters,
              cvss: vulnData.cvss,
              epss: vulnData.epss,
            }
          : null;

        return { data: mappedData, status: vulnStatus };
      }
      case 'Seed':
        return { data: seedData, status: seedStatus };
      case 'ActiveDirectory':
        return {
          data: adData,
          status: adData ? ('success' as const) : ('pending' as const),
        };
      default:
        return { data: null, status: 'idle' as const };
    }
  }, [
    entityType,
    assetData,
    assetStatus,
    techData,
    techStatus,
    vulnData,
    vulnStatus,
    seedData,
    seedStatus,
    adData,
  ]);

  // Apply context-based exclusions (for Asset filters primarily)
  const contextFilteredData = useMemo(() => {
    if (!data || entityType !== 'Asset') return data;

    if (typeof data === 'object' && !Array.isArray(data)) {
      return applyContextExclusions(
        data as Record<string, unknown>,
        entityType,
        context
      );
    }
    return data;
  }, [data, entityType, context]);

  // Generate sorted options
  const sortedOptions = useMemo(() => {
    return getEntitySortedOptions(entityType, contextFilteredData, status);
  }, [entityType, contextFilteredData, status]);

  return {
    filterOptions: contextFilteredData,
    availableFilters:
      entityType === 'Asset' &&
      typeof contextFilteredData === 'object' &&
      !Array.isArray(contextFilteredData)
        ? (contextFilteredData as Record<string, FilterOption[]>)
        : null,
    sortedOptions,
    status,
  };
}
